import { useParams } from 'react-router-dom';
import { Button, Input, Modal } from 'edozvola-frontend-lib/dist/ui/components';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { FormikProvider, useFormik } from 'formik';
import { Loader } from 'app/shared/loader/loader';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import React from 'react';
import {
  initialSiteSupervisorData,
  SiteSupervisor,
} from 'app/edozvola/cases/construction-participants/model/construction-participants.model';
import { siteSupervisorValidationSchema } from 'app/edozvola/cases/construction-participants/model/construction-participants.schema';
import { ChamberMemberCodebookModel } from 'app/shared/model/shared.model';
import {
  fetchSiteSupervisorQuery,
  saveSiteSupervisorQuery,
} from 'app/edozvola/cases/construction-participants/service/construction-participants.queries';
import {
  fetchChamberMembersQuery,
  fetchSubjectTypeQuery,
  fetchSupervisorChamberMembersQuery
} from 'app/edozvola/codebooks/codebooks.queries';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import { SUBJECT_TYPE_ENUM } from 'app/shared/enumeration/enums';
import { LegalEntityModel } from 'app/shared/oib/oib.model';
import AddressInput from 'app/shared/address-input/address-input';
import { formatAddress } from 'app/shared/address-input/address-helper';
import OibInput from 'app/shared/oib/oib-input';
import { initialAddress } from 'app/shared/address-input/model/address.model';

export interface ISiteSupervisorFormProps {
  idSiteSupervisor: string | null;
  setIdSiteSupervisor: any;
  readOnly: boolean;
}

const SiteSupervisorForm = (props: ISiteSupervisorFormProps) => {
  const { id: uuid } = useParams<{ id: string }>();
  const { data: chamberMemberOptions, isLoading: chamberMemberOptionsLoading } = fetchSupervisorChamberMembersQuery();
  const { data: subjectTypeOptions, isLoading: subjectTypeOptionsLoading } = fetchSubjectTypeQuery();
  const { data: siteSupervisorData, isLoading: siteSupervisorDataLoading } = fetchSiteSupervisorQuery(uuid, props.idSiteSupervisor);

  const formik = useFormik({
    initialValues: siteSupervisorData
      ? {
          ...siteSupervisorData,
          siteSupervisorOffice: {
            ...siteSupervisorData.siteSupervisorOffice,
            address:
              siteSupervisorData.siteSupervisorOffice.subjectType.length === 0
                ? initialAddress
                : siteSupervisorData.siteSupervisorOffice.address,
          },
        }
      : initialSiteSupervisorData,
    validationSchema: siteSupervisorValidationSchema,
    onSubmit: (values: SiteSupervisor) => saveSiteSupervisorMutation(values),
    enableReinitialize: true,
    validateOnMount: true,
  });

  const { mutate: saveSiteSupervisorMutation, isPending: isSiteSupervisorSaving } = saveSiteSupervisorQuery(uuid, () => closeModal());

  const closeModal = () => {
    props.setIdSiteSupervisor(null);
    formik.resetForm({ values: initialSiteSupervisorData });
  };

  const onChamberMemberChanged = (selected: ChamberMemberCodebookModel[]) => {
    formik.setFieldValue('chamberMember.oib', selected[0] ? selected[0].oib : '');
    formik.setFieldValue('chamberMember.authorizationNumber', selected);
  };

  const filteredSubjectTypeOptions = React.useMemo(() => {
    return subjectTypeOptions?.filter((opt: any) => opt.id !== SUBJECT_TYPE_ENUM.FIZICKA_OSOBA && opt.id !== SUBJECT_TYPE_ENUM.OPG);
  }, [subjectTypeOptions]);

  const oibFetched = (fetchedOibData: LegalEntityModel) => {
    formik.setFieldValue('siteSupervisorOffice.legalEntity', fetchedOibData);
  };

  const onOibChanged = (value: string) => {
    formik.setFieldValue('siteSupervisorOffice.legalEntity.companyName', '');
    formik.setFieldValue('siteSupervisorOffice.legalEntity.address', null);

    formik.setTouched({
      ...formik.touched,
      siteSupervisorOffice: {
        legalEntity: {
          oib: true,
        },
      },
    });
    formik.setFieldValue('siteSupervisorOffice.legalEntity.oib', value);
  };

  const onSubjectTypeChange = selected => {
    if (selected.length === 0) {
      formik.setFieldValue('siteSupervisorOffice.address', initialAddress);
    }
    onOibChanged('');
    formik.setFieldValue('siteSupervisorOffice.legalEntity.mb', '');
    formik.setFieldValue('siteSupervisorOffice.subjectType', selected);

    setTimeout(() => {
      formik.validateForm();
    }, 0);
  };

  const isOsobaSelectedEqual = (subjectType: SUBJECT_TYPE_ENUM) =>
    formik.values.siteSupervisorOffice?.subjectType &&
    formik.values.siteSupervisorOffice?.subjectType[0] &&
    formik.values.siteSupervisorOffice?.subjectType[0].id === subjectType;

  const isObrtSlobodnaDjelatnostIliOpgSelected =
    isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.OBRT) ||
    isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.SLOBODNA_DJELATNOST) ||
    isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.OPG);

  const phoneChanged = (event: any, field: string) => {
    formik.setFieldValue(field, event.target.value);
    event.preventDefault();
  };

  return (
    <>
      <Modal scrollable size={'xl'} isOpen={props.idSiteSupervisor !== null}>
        {props.readOnly ? (
          <ModalHeader toggle={() => closeModal()}>Pregled nadzornog inženjera</ModalHeader>
        ) : (
          <ModalHeader>{props.idSiteSupervisor === '' ? 'Unos nadzornog inženjera' : 'Uređivanje nadzornog inženjera'}</ModalHeader>
        )}
        <ModalBody>
          <FormikProvider value={formik}>
            {siteSupervisorDataLoading && <Loader />}
            {!siteSupervisorDataLoading && (
              <div className={'d-flex justify-content-between flex-column gap-3'}>
                <Row className={'pb-3'}>
                  <Col md={6}>
                    <Label labelText={'Broj ovlaštenja/Ime i prezime'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi broj ovlaštenja/ime i prezime'}
                      onItemSelect={onChamberMemberChanged}
                      selected={formik.values.chamberMember.authorizationNumber}
                      labelKey={'value'}
                      options={chamberMemberOptionsLoading ? [] : chamberMemberOptions}
                      disabled={props?.readOnly}
                      positionFixed
                      error={
                        formik.errors.chamberMember && formik.errors.chamberMember['authorizationNumber']
                          ? (formik.errors.chamberMember['authorizationNumber'] as string)
                          : ''
                      }
                      showErrorMessage
                      clearButton
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'OIB'} />
                    <Input disabled value={formik.values.chamberMember.oib} />
                  </Col>
                </Row>
                <div className={'form-header'}>Ured nadzornog inženjera</div>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Vrsta subjekta'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi vrstu subjekta'}
                      onItemSelect={onSubjectTypeChange}
                      selected={formik.values.siteSupervisorOffice?.subjectType}
                      labelKey={'value'}
                      clearButton
                      options={filteredSubjectTypeOptions}
                      disabled={props?.readOnly}
                      error={(formik.errors.siteSupervisorOffice?.subjectType as string) ?? ''}
                      showErrorMessage
                    />
                  </Col>
                  <Col md={6}>
                    {isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) ? (
                      <OibInput
                        value={formik.values.siteSupervisorOffice?.legalEntity?.oib ?? ''}
                        handleBlur={formik.handleBlur}
                        handleChange={event => onOibChanged(event.target.value)}
                        errors={formik.errors.siteSupervisorOffice?.legalEntity}
                        touched={formik.touched.siteSupervisorOffice?.legalEntity}
                        onOibFetched={oibFetched}
                        fetchLegalEntity={true}
                        disabled={
                          props.readOnly ||
                          !formik.values.siteSupervisorOffice?.subjectType ||
                          formik.values.siteSupervisorOffice?.subjectType?.length === 0
                        }
                      />
                    ) : (
                      isObrtSlobodnaDjelatnostIliOpgSelected && (
                        <>
                          <Label labelText="MB" />
                          <Input
                            value={formik.values.siteSupervisorOffice?.legalEntity?.mb}
                            id="mb"
                            name="mb"
                            maxLength={11}
                            disabled={props.readOnly}
                            onChange={event => formik.setFieldValue('siteSupervisorOffice.legalEntity.mb', event.target.value)}
                            error={
                              formik.errors.siteSupervisorOffice?.legalEntity && formik.errors.siteSupervisorOffice?.legalEntity['mb']
                                ? formik.errors.siteSupervisorOffice?.legalEntity['mb']
                                : ''
                            }
                          />
                        </>
                      )
                    )}
                  </Col>
                </Row>
                {(isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) || isObrtSlobodnaDjelatnostIliOpgSelected) && (
                  <Row>
                    <Col md={6}>
                      <Label labelText={'Naziv'} />
                      <Input
                        disabled={props.readOnly || isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA)}
                        value={formik.values.siteSupervisorOffice?.legalEntity.companyName}
                        error={
                          formik.errors.siteSupervisorOffice?.legalEntity && formik.errors.siteSupervisorOffice?.legalEntity['companyName']
                            ? formik.errors.siteSupervisorOffice?.legalEntity['companyName']
                            : ''
                        }
                        maxLength={200}
                        onChange={event => formik.setFieldValue('siteSupervisorOffice.legalEntity.companyName', event.target.value)}
                      />
                    </Col>
                  </Row>
                )}
                {isOsobaSelectedEqual(SUBJECT_TYPE_ENUM.PRAVNA_OSOBA) && formik.values.siteSupervisorOffice?.legalEntity?.address && (
                  <Row>
                    <Col md={6}>
                      <Label labelText="Adresa iz OIB-a" />
                      <span>{formatAddress(formik.values.siteSupervisorOffice?.legalEntity.address)}</span>
                    </Col>
                  </Row>
                )}
                <AddressInput
                  field={formik.values.siteSupervisorOffice?.address}
                  errors={formik.errors.siteSupervisorOffice?.address}
                  readOnly={formik.values.siteSupervisorOffice.subjectType?.length === 0 || props.readOnly}
                  setFieldValue={(field: string, value: any) => formik.setFieldValue(`siteSupervisorOffice.address.${field}`, value)}
                />
                <Row>
                  <Col md={6}>
                    <Label labelText={'Email'} />
                    <Input
                      value={formik.values.email}
                      id={'email'}
                      name={'email'}
                      error={formik.errors.email ?? ''}
                      disabled={props.readOnly}
                      maxLength={60}
                      onChange={event => formik.setFieldValue(`email`, event.target.value)}
                    />
                  </Col>
                  <Col md={6}>
                    <Label labelText={'Telefon'} />
                    <Input
                      value={formik.values.telephoneNumber}
                      id={'telephoneNumber'}
                      name={'telephoneNumber'}
                      disabled={props.readOnly}
                      maxLength={50}
                      onChange={e => phoneChanged(e, 'telephoneNumber')}
                    />
                  </Col>
                </Row>
              </div>
            )}
          </FormikProvider>
        </ModalBody>
        <ModalFooter>
          {!props.readOnly && (
            <>
              <Button label={'Odustani'} outline size={'lg'} onClick={closeModal} />
              <Button
                label={'Spremi'}
                size={'lg'}
                disabled={!formik.dirty || !formik.isValid}
                isLoading={isSiteSupervisorSaving}
                color={'info'}
                onClick={() => formik.handleSubmit()}
              />
            </>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
};

export default SiteSupervisorForm;
